// src/components/ModerationTab.js — Admin → Messaging → Moderation.
// UX member-replies.md §6 (APPROVED 2026-10-05). Design intent: "All clear" is
// the normal state; Danielle only ever acts on "Needs you", and each card
// carries its own context so she never has to open the thread to decide.
//
// Module-scope components only; hover via useState (never DOM mutation).

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { subscribeNeedsReview, subscribeHandled, moderateComment, lookupDoc } from '../services/moderation';

const SOFT = 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))';
const FAINT = 'color-mix(in srgb, var(--mom-charcoal) 52%, var(--mom-cream))';
const HAIR = 'color-mix(in srgb, var(--mom-charcoal) 9%, #fff)';
const SANS = 'var(--mom-font-sans)';
const SERIF = 'var(--mom-font-serif)';
const UNDO_MS = 6000;

const REASON_LABEL = { unkind: 'Unkind or hurtful', spam: 'Spam or selling', other: 'Something else' };

function whenLabel(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const day = d.toLocaleDateString('en-US', { weekday: 'short' });
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    const rel = mins < 60 ? `${Math.max(1, mins)}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;
    return `${day} · ${rel}`;
}
function whyText(e) {
    if (e.reason === 'reports') {
        const parts = Object.entries(e.reasonCounts || {}).map(([k, n]) => `${n > 1 ? `${n}× ` : ''}"${REASON_LABEL[k] || k}"`);
        return `${e.reportCount || 2} members reported it${parts.length ? ` as ${parts.join(', ')}` : ''}.`;
    }
    return "The AI wasn't sure.";
}

function ActionButton({ primary, onClick, children, disabled }) {
    const [hover, setHover] = useState(false);
    return (
        <button onClick={onClick} disabled={disabled} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={primary ? {
                border: 0, borderRadius: 99, padding: '11px 24px', fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: '#fff', cursor: 'pointer',
                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                boxShadow: hover ? '0 8px 22px color-mix(in srgb, var(--mom-primary) 38%, transparent)' : '0 6px 18px color-mix(in srgb, var(--mom-primary) 28%, transparent)',
                opacity: disabled ? 0.5 : 1,
            } : {
                border: 0, background: 'none', padding: '8px 0', fontFamily: SANS, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                color: hover ? 'var(--mom-charcoal)' : SOFT, opacity: disabled ? 0.5 : 1,
            }}>
            {children}
        </button>
    );
}

function NeedsCard({ entry, onKeep, onRemove, leaving }) {
    const [ctx, setCtx] = useState({ classTitle: '', parent: null });
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            entry.classId ? lookupDoc('liveClasses', entry.classId) : (entry.recordingId ? lookupDoc('classRecordings', entry.recordingId) : null),
            entry.parentCommentId ? lookupDoc('classComments', entry.parentCommentId) : null,
        ]).then(([cls, parent]) => { if (!cancelled) setCtx({ classTitle: (cls && cls.title) || 'Class', parent }); });
        return () => { cancelled = true; };
    }, [entry.classId, entry.recordingId, entry.parentCommentId]);
    const initials = (entry.authorName || '?').split(' ').map((n) => n.charAt(0).toUpperCase()).slice(0, 2).join('');
    const threadHref = entry.classId
        ? `/workouts?class=${encodeURIComponent(entry.classId)}&comment=${encodeURIComponent(entry.commentId)}`
        : `/workouts?recording=${encodeURIComponent(entry.recordingId || '')}&comment=${encodeURIComponent(entry.commentId)}`;
    return (
        <div style={{
            background: '#fff', borderRadius: 20, padding: '22px 24px', marginBottom: 16, boxShadow: '0 8px 26px rgba(62,47,47,.06)',
            transition: 'opacity 0.35s ease, transform 0.35s ease', opacity: leaving ? 0 : 1, transform: leaving ? 'translateX(24px)' : 'none',
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                <span style={{ fontFamily: SANS, fontSize: 10.5, letterSpacing: '0.13em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--mom-primary-dark, var(--mom-primary))' }}>
                    {entry.reason === 'reports' ? `Reported by ${entry.reportCount || 2} members` : 'Flagged · needs a human eye'}
                </span>
                <div style={{ textAlign: 'right', fontFamily: SANS, fontSize: 12, color: SOFT, lineHeight: 1.5 }}>
                    <b style={{ color: 'var(--mom-charcoal)', fontWeight: 600 }}>{ctx.classTitle}</b><br />{whenLabel(entry.createdAt)}
                </div>
            </div>
            {ctx.parent ? (
                <div style={{ margin: '14px 0 12px', padding: '10px 14px', borderLeft: '2px solid var(--mom-primary-light, #f0d6d4)', background: 'var(--mom-cream)', borderRadius: '0 10px 10px 0', fontFamily: SANS, fontSize: 12.5, color: SOFT }}>
                    Replying to <b style={{ color: 'var(--mom-charcoal)' }}>{ctx.parent.isCoach ? 'Danielle' : ctx.parent.userName}</b>: "{String(ctx.parent.text || '').slice(0, 140)}"
                </div>
            ) : <div style={{ height: 14 }} />}
            <div style={{ display: 'flex', gap: 11 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--mom-cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: SANS, fontSize: 12, fontWeight: 700, color: 'var(--mom-charcoal)', flexShrink: 0 }}>{initials}</div>
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: 'var(--mom-charcoal)' }}>{entry.authorName || 'Member'}</div>
                    <p style={{ fontFamily: SANS, fontSize: 14, lineHeight: 1.6, color: 'var(--mom-charcoal)', margin: '2px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.text}</p>
                </div>
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${HAIR}`, fontFamily: SANS, fontSize: 13, lineHeight: 1.65, color: SOFT }}>
                <b style={{ color: 'var(--mom-charcoal)', fontWeight: 600 }}>Why it's here:</b> {whyText(entry)}
                {entry.aiRead && <><br /><b style={{ color: 'var(--mom-charcoal)', fontWeight: 600 }}>AI read:</b> {entry.aiRead}</>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginTop: 18, flexWrap: 'wrap' }}>
                <ActionButton primary onClick={() => onKeep(entry)} disabled={leaving}>Keep it up</ActionButton>
                <ActionButton onClick={() => onRemove(entry)} disabled={leaving}>Remove for good</ActionButton>
                <a href={threadHref} style={{ marginLeft: 'auto', fontFamily: SANS, fontSize: 13, fontWeight: 600, color: 'var(--mom-primary-dark, var(--mom-primary))', textDecoration: 'none' }}>View in thread ↗</a>
            </div>
        </div>
    );
}

function HandledRow({ entry, onRestore }) {
    const [hover, setHover] = useState(false);
    const cat = entry.aiCategory ? entry.aiCategory.charAt(0).toUpperCase() + entry.aiCategory.slice(1) : 'Flagged';
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(90px,140px) 1fr auto auto auto', gap: 14, alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${HAIR}`, fontFamily: SANS, fontSize: 12.5, color: SOFT }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.authorName || 'Member'}</span>
            <span title={entry.aiRead || ''} style={{ color: 'var(--mom-charcoal)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.text}</span>
            <span style={{ fontSize: 11, background: 'var(--mom-cream)', borderRadius: 7, padding: '3px 8px', whiteSpace: 'nowrap' }}>{cat}</span>
            <span style={{ whiteSpace: 'nowrap' }}>{new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            <button onClick={() => onRestore(entry)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                style={{ border: 0, background: 'none', padding: '6px 0', fontFamily: SANS, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: hover ? 'var(--mom-primary)' : 'var(--mom-primary-dark, var(--mom-primary))' }}>Restore</button>
        </div>
    );
}

export default function ModerationTab({ getToken }) {
    const [needs, setNeeds] = useState(null);
    const [handled, setHandled] = useState([]);
    const [handledOpen, setHandledOpen] = useState(false);
    const [leaving, setLeaving] = useState({}); // id → true while fading out
    const [gone, setGone] = useState({});       // id → true once collapsed (until the snapshot drops it)
    const [toast, setToast] = useState(null);
    const pendingRemove = useRef(new Map());    // id → { timer, commit } during the Undo window

    useEffect(() => subscribeNeedsReview(setNeeds), []);
    useEffect(() => subscribeHandled(setHandled), []);
    // Leaving Admin mid-Undo still applies the removal.
    useEffect(() => () => { pendingRemove.current.forEach((p) => { clearTimeout(p.timer); p.commit(); }); }, []);

    const say = useCallback((t) => { setToast(t); setTimeout(() => setToast((cur) => (cur === t ? null : cur)), t.action ? UNDO_MS : 2600); }, []);
    const fadeOut = (id) => { setLeaving((p) => ({ ...p, [id]: true })); setTimeout(() => setGone((p) => ({ ...p, [id]: true })), 360); };
    const bringBack = (id) => {
        setLeaving((p) => { const n = { ...p }; delete n[id]; return n; });
        setGone((p) => { const n = { ...p }; delete n[id]; return n; });
    };

    const keep = async (e, restore = false) => {
        fadeOut(e.id);
        const ok = await moderateComment(e.commentId, restore ? 'restore' : 'keep', getToken);
        if (!ok) { bringBack(e.id); say({ message: "Couldn't save that — please try again." }); return; }
        say({ message: restore ? "Restored. It's visible again." : 'Back up for everyone.' });
    };
    const remove = (e) => {
        fadeOut(e.id);
        let done = false;
        const commit = () => { if (done) return; done = true; pendingRemove.current.delete(e.id); moderateComment(e.commentId, 'remove', getToken); };
        const timer = setTimeout(commit, UNDO_MS);
        pendingRemove.current.set(e.id, { timer, commit });
        say({
            message: 'Removed.', action: 'Undo',
            onAction: () => { clearTimeout(timer); done = true; pendingRemove.current.delete(e.id); bringBack(e.id); setToast(null); },
        });
    };

    const shown = (needs || []).filter((e) => !gone[e.id]);
    const n = (needs || []).filter((e) => !leaving[e.id]).length;

    return (
        <div style={{ maxWidth: 820 }}>
            <h2 style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 600, fontSize: 30, margin: 0, color: 'var(--mom-charcoal)' }}>Moderation</h2>
            <div style={{ width: 44, height: 2, background: 'var(--mom-primary)', opacity: 0.45, margin: '10px 0 14px', borderRadius: 1 }} />
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 17, color: SOFT }}>
                {needs === null ? ' ' : n === 0 ? 'All clear — nothing needs you.' : `${n} comment${n === 1 ? ' needs' : 's need'} your eye.`}
            </div>

            <SectionLabel>Needs you</SectionLabel>
            {needs === null ? null : n === 0 ? (
                <div style={{ textAlign: 'center', padding: '44px 20px', animation: 'fadeUp 0.3s ease' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', margin: '0 auto 14px', background: 'radial-gradient(circle at 35% 30%, #fff, var(--mom-primary-light, #f0d6d4))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--mom-primary) 18%, #fff)' }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary-dark, #c05a7a)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </div>
                    <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 19, margin: 0, color: 'var(--mom-charcoal)' }}>All clear. Nice.</p>
                </div>
            ) : shown.map((e) => (
                <NeedsCard key={e.id} entry={e} leaving={!!leaving[e.id]} onKeep={(x) => keep(x)} onRemove={remove} />
            ))}

            <SectionLabel>Handled for you</SectionLabel>
            <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 16px rgba(62,47,47,.05)' }}>
                <button onClick={() => setHandledOpen((v) => !v)} aria-expanded={handledOpen}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', border: 0, background: 'none', padding: '16px 20px', fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: 'var(--mom-charcoal)', cursor: 'pointer' }}>
                    Handled for you <span style={{ color: SOFT, fontWeight: 400 }}>· {handled.length} this month {handledOpen ? '▾' : '›'}</span>
                </button>
                {handledOpen && (handled.filter((e) => !gone[e.id]).length
                    ? handled.filter((e) => !gone[e.id]).map((e) => <HandledRow key={e.id} entry={e} onRestore={(x) => keep(x, true)} />)
                    : <div style={{ padding: '12px 20px 16px', borderTop: `1px solid ${HAIR}`, fontFamily: SANS, fontSize: 12.5, color: SOFT }}>Nothing handled this month.</div>)}
            </div>
            <p style={{ fontFamily: SANS, fontSize: 12, color: FAINT, marginTop: 12, lineHeight: 1.6 }}>
                The AI handled these on its own, so they didn't ping you. Restore anything it got wrong. This list clears itself after 30 days.
            </p>

            {toast && (
                <div role="status" style={{ position: 'fixed', left: '50%', bottom: 30, transform: 'translateX(-50%)', zIndex: 3300, background: 'var(--mom-charcoal)', color: '#fff', fontFamily: SANS, fontSize: 13, padding: '11px 18px', borderRadius: 99, display: 'flex', gap: 16, animation: 'fadeUp 0.2s ease' }}>
                    <span>{toast.message}</span>
                    {toast.action && <button onClick={toast.onAction} style={{ border: 0, background: 'none', color: 'var(--mom-primary-light, #f0d6d4)', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0 }}>{toast.action}</button>}
                </div>
            )}
        </div>
    );
}

function SectionLabel({ children }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '30px 0 12px', fontFamily: SANS, fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: FAINT }}>
            {children}<span style={{ flex: 1, height: 1, background: HAIR }} />
        </div>
    );
}
